import React, { useEffect, useState } from 'react';
import truncate from 'lodash/truncate';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { richText } from '../../../util/richText';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { propTypes } from '../../../util/types';
import CouponCard from '../../../components/CouponCard/CouponCard';
import { useLocation, useHistory } from 'react-router-dom';

import { AvatarLarge, NamedLink, InlineTextButton } from '../../../components';
import { H1 } from '../../../components/Heading/Heading';
import { handleSsubmit } from '../ListingPage.shared';
import { handleSubmit } from '../ListingPage.shared';

import css from './UserCard.module.css';

// Approximated collapsed size so that there are ~three lines of text
// in the desktop layout in the author section of the ListingPage.
const BIO_COLLAPSED_LENGTH = 170;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const truncated = s => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,

    // Allow truncated text end only in specific characters. This will
    // make the truncated text shorter than the length if the original
    // text has to be shortened and the substring ends in a separator.
    //
    // This ensures that the final text doesn't get cut in the middle
    // of a word.
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

const ExpandableBio = props => {
  const [expand, setExpand] = useState(false);
  const { className, bio } = props;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const truncatedBio = richText(truncated(bio), {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

  const handleShowMoreClick = () => {
    setExpand(true);
  };
  const showMore = (
    <InlineTextButton
      rootClassName={css.showMore}
      onClick={handleShowMoreClick}
    >
      <FormattedMessage id="UserCard.showFullBioLink" />
    </InlineTextButton>
  );
  return (
    <p className={className}>
      {expand ? bioWithLinks : truncatedBio}
      {bio.length >= BIO_COLLAPSED_LENGTH && !expand ? showMore : null}
    </p>
  );
};

/**
 * The UserCard component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.user | propTypes.currentUser} props.user - The user
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {function} props.onContactUser - The on contact user function
 * @param {boolean} [props.showContact] - Whether to show the contact user button
 * @returns {JSX.Element} user card component
 */
const UserCard = props => {
  const [mounted, setMounted] = useState(false);
  // Add state for voucher form fields
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    rootClassName,
    className,
    user,
    currentUser,
    onContactUser,
    showContact = true,
  
    routes,
    params,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onFetchTransactionLineItems,
    isOwnListing,
  } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser
    ? ensureCurrentUser(user)
    : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id &&
    ensuredCurrentUser.id &&
    ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio } = ensuredUser.attributes.profile;

  const handleContactUserClick = () => {
    onContactUser(user);
  };
  const history = useHistory();
const location = useLocation();

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });
  

  const pathname = location.pathname;
  const parts = pathname.split('/');
  // const slug = parts[2]; // e.g. "so-clean-you-can-eat-of-it"
  // const id = parts[3];

  const separator =
    (mounted && isCurrentUser) || !showContact ? null : (
      <span className={css.linkSeparator}>•</span>
    );

  const contact = showContact ? (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      enforcePagePreloadFor="SignupPage"
    >
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  ) : null;
  const goToCheckout = () => {
  // This gets the current path like /l/super-duper-cleaning/68805259-xxxx-xxxx
  const basePath = location.pathname;

  // Redirect to the checkout path
  history.push(`${basePath}/checkout`);
};

  const editProfileMobile = (
    <span className={css.editProfileMobile}>
      <span className={css.linkSeparator}>•</span>
      <NamedLink name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </span>
  );

  const editProfileDesktop =
    mounted && isCurrentUser ? (
      <NamedLink className={css.editProfileDesktop} name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    ) : null;

  const links = ensuredUser.id ? (
    <p className={linkClasses}>
      <NamedLink
        className={css.link}
        name="ProfilePage"
        params={{ id: ensuredUser.id.uuid }}
      >
        <FormattedMessage id="UserCard.viewProfileLink" />
      </NamedLink>
      {separator}
      {mounted && isCurrentUser ? editProfileMobile : contact}
    </p>
  ) : null;

  const couponOptions = [
    { code: 'SAVE50', amount: 50 },
    { code: 'WELCOME100', amount: 100 },
    { code: 'FESTIVE200', amount: 200 },
  ];
  const now = new Date();
 
// Convert to UTC
// const bookingStart = new Date(Date.UTC(2025, 7, 1, 12, 30));
// const bookingEnd = new Date(Date.UTC(2025, 7, 1, 14, 30));


const onNextPayClick = () => {

  
  const res = onFetchTransactionLineItems({
    orderData :{
     
      recipientName,
      recipientEmail,
      senderName,
      message,
      acceptedTerms,
      selectedCoupon,
      // quantity: selectedHours,
    },
      listingId : params.id,
      isOwnListing,                 
  })
  const submit = handleSsubmit({
    history,
    params,
    currentUser,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routes,
  });

  // Map coupon codes to number of hours
  const couponToHours = {
    SAVE50: 2,
    WELCOME100: 4,
    FESTIVE200: 8,
  };

  const selectedHours = couponToHours[selectedCoupon] || 1;

  submit({
   
    recipientName,
    recipientEmail,
    senderName,
    message,
    acceptedTerms,
    selectedCoupon,
    quantity: selectedHours,
    
  });
};

  return (
    <div className={classes}>
      {/* <div className={css.voucherHeader}>
        <H1>
          <FormattedMessage id="VoucherPurchasePage.header" defaultMessage="Practical help when the village isn’t just down the road" />
        </H1>
        <p>
          <FormattedMessage id="VoucherPurchasePage.description" defaultMessage="You or your loved one will receive 2 hours of help per voucher, with whichever preferences are nominated. Services include laundry, ironing, home organisation, cleaning, meal preparation, child-care, gardening or dog walking." />
        </p>
      </div> */}
      {/* Voucher Send to / From form */}
      
      <div className={css.content}>
        <AvatarLarge className={css.avatar} user={user} />
        <div className={css.info}>
          <div className={css.headingRow}>
            <FormattedMessage id="UserCard.heading" values={{ name: displayName }} />
            {editProfileDesktop}
          </div>
          {hasBio ? <ExpandableBio className={css.desktopBio} bio={bio} /> : null}
          {links}
          
        </div>
      </div>
      {hasBio ? <ExpandableBio className={css.mobileBio} bio={bio} /> : null}
    </div>
  );
};

export default UserCard;
